import { getContext, invoke, getFrameId, reload } from './core';
import { initFeatureFlags, trackFeatureFlagEvent } from './featureFlags';
import { iframeAnalytics, trackObjectStoreAction } from './telemetry';
import { bridgeConfigDelegates } from './bridge-config-delegates';
import { fetchProduct, type FetchProductPayload, type FetchProductResponse } from './fetchProduct';
import { on, emit, type SubscriptionHandle } from './events';
import { onPublic, emitPublic } from './publicEvents';
import { subscribeRealtimeChannel, publishRealtimeChannel } from './realtime';
import { getUrl, navigate } from './navigation';
import type { History } from 'history';
import type { ForgeDoc } from '@atlassian/forge-ui-types';
import type { FlagOptions } from '../../custom-ui';
import type { NavigatePayload } from '../../custom-ui/bridge';
import type { FeatureFlagEnv } from '../../custom-ui/bridge/types';
import type { ViewContext } from '../../custom-ui/iframe/types';
import type {
	SubscribeRealtimeChannelPayload,
	PublishRealtimeChannelPayload,
} from '../../web-client';
import type { BridgeContext, BridgeServices } from '../types';
import type { TBridgeMethod } from './types';
import { createHistory } from './history';

export const bridgeMethods: {
	// Core methods
	getContext: TBridgeMethod<Pick<BridgeContext, 'viewContext'>, void, ViewContext>;
	invoke: TBridgeMethod<
		Pick<BridgeContext, 'resolverContext' | 'extensionPayload' | 'contextIds'> & {
			services: Pick<BridgeServices, 'invoker' | 'getContextToken' | 'tracing' | 'onThreeLO'>;
		},
		{
			functionKey: string;
			payload: object;
		},
		object
	>;
	getFrameId: TBridgeMethod<Pick<BridgeContext, 'extension'>, void, string | null>;
	reload: TBridgeMethod<Pick<BridgeContext, never>, void, void>;
	fetchProduct: TBridgeMethod<
		Pick<BridgeContext, never> & {
			services: Pick<BridgeServices, 'productFetchClient'>;
		},
		FetchProductPayload,
		FetchProductResponse
	>;
	// Event methods
	on: TBridgeMethod<
		Pick<BridgeContext, 'extension' | 'extensionDetails' | 'claimBridgeResource'>,
		{
			callback: (payload?: any) => Promise<any>;
			event: string;
		},
		SubscriptionHandle
	>;
	emit: TBridgeMethod<
		Pick<BridgeContext, 'extensionDetails'>,
		{
			event: string;
			payload?: any;
		},
		void
	>;
	// Public event methods
	onPublic: TBridgeMethod<
		Pick<BridgeContext, 'extensionDetails' | 'claimBridgeResource'>,
		{
			callback: (payload?: any) => Promise<any>;
			event: string;
		},
		SubscriptionHandle
	>;
	emitPublic: TBridgeMethod<
		Pick<BridgeContext, 'contextIds' | 'extensionDetails'> & {
			services: Pick<BridgeServices, 'relayEnvironment'>;
		},
		{
			event: string;
			payload?: any;
		},
		void
	>;
	// Realtime methods
	subscribeRealtimeChannel: TBridgeMethod<
		Pick<BridgeContext, 'extension' | 'resolverContext' | 'claimBridgeResource'> & {
			services: Pick<BridgeServices, 'getContextToken'>;
		},
		SubscribeRealtimeChannelPayload,
		{
			unsubscribe: () => void;
		}
	>;
	publishRealtimeChannel: TBridgeMethod<
		Pick<BridgeContext, 'extension' | 'resolverContext'> & {
			services: Pick<BridgeServices, 'getContextToken' | 'relayEnvironment'>;
		},
		PublishRealtimeChannelPayload,
		| {
				eventId: string;
				eventTimestamp: string;
		  }
		| undefined
	>;
	// Navigation methods
	getUrl: TBridgeMethod<
		Pick<BridgeContext, 'viewContext' | 'extension' | 'contextIds' | 'extensionDetails'> & {
			services: Pick<BridgeServices, 'relayEnvironment'>;
		},
		{
			type: 'same-tab' | 'new-tab';
		} & ((
			| {
					contentId: string;
			  }
			| {
					contentType: 'page' | 'blogpost';
					spaceKey: string;
			  }
			| {
					contentType: 'customContent';
					moduleKey: string;
			  }
			| {
					contentId: string;
					version?: number;
			  }
			| {
					dashboardId: string;
			  }
			| {
					issueKey: string;
			  }
			| ({
					moduleKey?: string;
			  } & Record<string, unknown>)
			| {
					projectKey: string;
			  }
			| {
					spaceKey: string;
			  }
			| {
					accountId: string;
			  }
		) & {
			target:
				| 'contentEdit'
				| 'contentList'
				| 'contentView'
				| 'dashboard'
				| 'issue'
				| 'module'
				| 'projectSettingsDetails'
				| 'spaceView'
				| 'userProfile';
		}),
		string | null
	>;
	navigate: TBridgeMethod<
		Pick<BridgeContext, 'viewContext' | 'extension' | 'contextIds' | 'extensionDetails'> & {
			services: Pick<BridgeServices, 'relayEnvironment' | 'overlayController' | 'analytics'>;
		},
		| ({
				type: 'same-tab' | 'new-tab';
		  } & ((
				| {
						contentId: string;
				  }
				| {
						contentType: 'page' | 'blogpost';
						spaceKey: string;
				  }
				| {
						contentType: 'customContent';
						moduleKey: string;
				  }
				| {
						contentId: string;
						version?: number;
				  }
				| {
						dashboardId: string;
				  }
				| {
						issueKey: string;
				  }
				| ({
						moduleKey?: string;
				  } & Record<string, unknown>)
				| {
						projectKey: string;
				  }
				| {
						spaceKey: string;
				  }
				| {
						accountId: string;
				  }
		  ) & {
				target:
					| 'contentEdit'
					| 'contentList'
					| 'contentView'
					| 'dashboard'
					| 'issue'
					| 'module'
					| 'projectSettingsDetails'
					| 'spaceView'
					| 'userProfile';
		  }))
		| NavigatePayload,
		void
	>;
	// History methods
	createHistory: TBridgeMethod<Pick<BridgeContext, 'bridgeConfig'>, void, History | undefined>;
	// Feature flag methods
	initFeatureFlags: TBridgeMethod<
		Pick<BridgeContext, 'extension' | 'contextIds' | 'extensionDetails' | 'environment'> & {
			services: Pick<BridgeServices, 'getContextToken' | 'tracing' | 'analytics'>;
		},
		{
			config: unknown;
			user?: {
				statsigEnvironment?: {
					tier: FeatureFlagEnv;
				};
			} & Record<string, unknown>;
		},
		object
	>;
	trackFeatureFlagEvent: TBridgeMethod<
		Pick<BridgeContext, 'extension' | 'extensionDetails'> & {
			services: Pick<BridgeServices, 'analytics'>;
		},
		{
			properties: {
				environment: string;
				name?: string;
				success: unknown;
			};
			type: string;
		},
		void
	>;
	// Telemetry methods
	'iframe-analytics': TBridgeMethod<
		Pick<BridgeContext, never> & {
			services: Pick<BridgeServices, 'analytics'>;
		},
		unknown,
		void
	>;
	trackObjectStoreAction: TBridgeMethod<
		Pick<BridgeContext, 'extension' | 'extensionDetails'> & {
			services: Pick<BridgeServices, 'analytics'>;
		},
		{
			action: 'upload' | 'download' | 'getMetadata' | 'delete';
		},
		void
	>;
	changeWindowTitle: TBridgeMethod<Pick<BridgeContext, 'bridgeConfig'>, string>;
	close: TBridgeMethod<Pick<BridgeContext, 'bridgeConfig'>, void>;
	closeFlag: TBridgeMethod<Pick<BridgeContext, 'bridgeConfig'>, Pick<FlagOptions, 'id'>>;
	emitReadyEvent: TBridgeMethod<Pick<BridgeContext, 'bridgeConfig'>, void>;
	onClose: TBridgeMethod<Pick<BridgeContext, 'bridgeConfig'>, void>;
	onError: TBridgeMethod<
		Pick<BridgeContext, 'bridgeConfig'>,
		{
			error: Error;
		}
	>;
	open: TBridgeMethod<Pick<BridgeContext, 'bridgeConfig'>, void>;
	openModal: TBridgeMethod<Pick<BridgeContext, 'bridgeConfig'>, any>;
	reconcile: TBridgeMethod<
		Pick<BridgeContext, 'bridgeConfig'>,
		{
			forgeDoc: ForgeDoc;
		}
	>;
	refresh: TBridgeMethod<Pick<BridgeContext, 'bridgeConfig'>, void>;
	showFlag: TBridgeMethod<Pick<BridgeContext, 'bridgeConfig'>, FlagOptions>;
	submit: TBridgeMethod<Pick<BridgeContext, 'bridgeConfig'>, void>;
} = {
	...bridgeConfigDelegates,
	// Core methods
	getContext,
	invoke,
	getFrameId,
	reload,
	fetchProduct,
	// Event methods
	on,
	emit,
	// Public event methods
	onPublic,
	emitPublic,
	// Realtime methods
	subscribeRealtimeChannel,
	publishRealtimeChannel,
	// Navigation methods
	getUrl,
	navigate,
	// History methods
	createHistory,
	// Feature flag methods
	initFeatureFlags,
	trackFeatureFlagEvent,
	// Telemetry methods
	'iframe-analytics': iframeAnalytics,
	trackObjectStoreAction,
};
